import { useRequiredContext } from "@redotech/react-util/context";
import { ScreenSize } from "@redotech/redo-model/email-builder";
import {
  GameType,
  prettyGameType,
  TrackingPageGameBlock,
} from "@redotech/redo-model/order-tracking/tracking-page-definition";
import { KAPLAYCtx } from "kaplay";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { RedoSpinner } from "../../../arbiter-components/loading/redo-spinner";
import { Flex } from "../../../flex";
import { Text } from "../../../text";
import { useIsMobile } from "../../../utils/check-mobile";
import { TrackingPageContext } from "../../context/tracking-page-context";
import { getBlockStyles, getHeaderStyles } from "../shared/block-styles";
import { BlockWrapper } from "../shared/block-wrapper";
import { cleanupCrewGame } from "./cleanup-crew-game";
import { deliveryDashGame } from "./delivery-dash/game-engine";
import { DiscountCodeDisplay } from "./discount-code-display";
import { flappyFreightGame } from "./flappy-freight-game";
import { galagaGame } from "./galaga-game";
import { GameCompleteCallback, GameControls } from "./game-controls";
import * as gameCss from "./game.module.css";
import { useDynamicSpriteUpdates } from "./hooks/use-dynamic-sprite-updates";
import { useGameCleanup } from "./hooks/use-game-cleanup";
import { useGameInitialization } from "./hooks/use-game-initialization";
import { useInitialSprites } from "./hooks/use-initial-sprites";
import { useLevelUpdates } from "./hooks/use-level-updates";
import { tinyPackageGame } from "./tiny-package/game-engine";

export enum GameState {
  Loading = "loading",
  Welcome = "welcome",
  Gameplay = "gameplay",
  Reward = "reward",
}

export enum VictoryState {
  VICTORY = "victory",
  FAILURE = "failure",
  IN_PROGRESS = "inProgress",
}

export interface CustomSprite {
  url: string;
  verticalOffset: number;
}

export interface TrackingPageGame {
  gameType: GameType;
  initializeGame: (
    k: KAPLAYCtx,
    onGameComplete: GameCompleteCallback,
    blockProps: TrackingPageGameBlock,
    playerSprite?: CustomSprite | null,
    npcSprite?: CustomSprite | null,
  ) => GameControls;
  strings: {
    victory?: string;
    failure?: string;
    credits?: string;
    instructions?: string;
  };
  showControls?: boolean;
}

const games: Record<GameType, TrackingPageGame> = {
  [GameType.DELIVERY_DASH]: deliveryDashGame,
  [GameType.GALAGA]: galagaGame,
  [GameType.CLEANUP_CREW]: cleanupCrewGame,
  [GameType.FLAPPY_FREIGHT]: flappyFreightGame,
  [GameType.TINY_PACKAGE]: tinyPackageGame,
};

export const GameBlock = memo(function GameBlock(
  blockProps: TrackingPageGameBlock,
) {
  const { environment, pageSettings, screenSize } =
    useRequiredContext(TrackingPageContext);
  const isMobile = useIsMobile() || screenSize === ScreenSize.MOBILE;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [gameState, setGameState] = useState<GameState>(GameState.Loading);
  const [score, setScore] = useState<number | undefined>(undefined);
  const [showCredits, setShowCredits] = useState(false);
  const [victoryState, setVictoryState] = useState<VictoryState | undefined>(
    VictoryState.IN_PROGRESS,
  );
  const game = useMemo(() => games[blockProps.gameType], [blockProps.gameType]);

  const { kaplayInstance, resizeObserverRef, gameInitialized, cleanupGame } =
    useGameCleanup();

  const handleGameComplete: GameCompleteCallback = ({
    finalScore,
    victoryState,
  }) => {
    setScore(finalScore);
    setVictoryState(victoryState);
    setGameState(GameState.Reward);
  };

  const { playerSprite, npcSprite } = useInitialSprites(blockProps, gameState);

  const { gameControls, setGameControls } = useGameInitialization(
    canvasRef,
    game,
    blockProps,
    gameState,
    gameInitialized,
    kaplayInstance,
    resizeObserverRef,
    handleGameComplete,
    playerSprite,
    npcSprite,
  );

  const { setCurrentPlayerSprite, setCurrentNpcSprite } =
    useDynamicSpriteUpdates(blockProps, gameState, gameControls);

  useLevelUpdates(blockProps, gameControls, gameState);

  // Effect that handles game lifecycle when gameType changes
  useEffect(() => {
    cleanupGame(
      setGameControls,
      setScore,
      setShowCredits,
      setVictoryState,
      setCurrentPlayerSprite,
      setCurrentNpcSprite,
    );
    setGameState(GameState.Loading);

    const initializeGameSetup = async () => {
      const loadingTimer = setTimeout(() => {
        setGameState(GameState.Welcome);
      }, 500);

      return () => clearTimeout(loadingTimer);
    };

    initializeGameSetup().catch((error) => {
      console.error("Game initialization failed:", error);
    });

    return () =>
      cleanupGame(
        setGameControls,
        setScore,
        setShowCredits,
        setVictoryState,
        setCurrentPlayerSprite,
        setCurrentNpcSprite,
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blockProps.gameType, cleanupGame]);

  const headerStyles = getHeaderStyles(pageSettings);
  const blockStyles = getBlockStyles(pageSettings);

  const handleStartGame = () => {
    setGameState(GameState.Gameplay);
  };

  const handlePlayAgain = () => {
    cleanupGame(
      setGameControls,
      () => setScore(0),
      setShowCredits,
      setVictoryState,
      setCurrentPlayerSprite,
      setCurrentNpcSprite,
    );
    setGameState(GameState.Gameplay);
  };

  const renderContent = () => {
    switch (gameState) {
      case GameState.Loading:
        return (
          <Flex
            align="center"
            dir="column"
            flex={1}
            gap="md"
            justify="center"
            p="lg"
          >
            <Text fontSize="lg" fontWeight="bold" style={headerStyles}>
              Loading Game...
            </Text>
            <Flex align="center" justify="center" p="xl">
              <RedoSpinner size="md" style="track" />
            </Flex>
          </Flex>
        );

      case GameState.Welcome:
        return (
          <Flex
            align="center"
            dir="column"
            flex={1}
            gap="lg"
            justify="center"
            p="lg"
          >
            <Text fontSize="xl" fontWeight="bold" style={headerStyles}>
              {prettyGameType[blockProps.gameType]}
            </Text>
            <Text fontSize="md" style={headerStyles} textAlign="center">
              {game.strings.instructions}
            </Text>
            <button
              className={gameCss.startGameButton}
              onClick={handleStartGame}
            >
              Start Game
            </button>
          </Flex>
        );

      case GameState.Gameplay:
        return (
          <Flex
            dir="column"
            flex={1}
            gap="none"
            style={{ minHeight: isMobile ? 500 : undefined }}
          >
            {/* Header */}
            <Flex justify="center" p="sm">
              <Text fontSize="sm" fontWeight="semibold" style={headerStyles}>
                {prettyGameType[game.gameType]}
              </Text>
            </Flex>

            {/* Game Canvas */}
            <Flex
              align="center"
              basis={0}
              flex={1}
              justify="center"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              overflow="hidden"
              style={{
                minHeight: isMobile ? 300 : 0,
                borderRadius: !game.showControls
                  ? `0 0 ${blockStyles.borderRadius}px ${blockStyles.borderRadius}px`
                  : undefined,
              }}
            >
              <canvas
                ref={canvasRef}
                style={{ width: "100%", height: "100%" }}
              />
            </Flex>

            {/* Compact Arcade Controls */}
            {game.showControls && (
              <Flex
                dir="column"
                gap="xs"
                overflow="hidden"
                p="sm"
                style={{
                  backgroundColor: "#222",
                  borderRadius: `0px 0px ${blockStyles.borderRadius}px ${blockStyles.borderRadius}px`,
                }}
                w="full"
              >
                <Flex gap="xs" justify="center">
                  <button
                    className={gameCss.controlsArrowKeys}
                    onPointerDown={() => gameControls?.moveUp()}
                    onPointerUp={() => gameControls?.stopMovement()}
                  >
                    ↑
                  </button>
                </Flex>
                <Flex gap="xs" justify="center">
                  <button
                    className={gameCss.controlsArrowKeys}
                    onPointerDown={() => gameControls?.moveLeft()}
                    onPointerUp={() => gameControls?.stopMovement()}
                  >
                    ←
                  </button>
                  <button
                    className={gameCss.controlsArrowKeys}
                    onPointerDown={() => gameControls?.moveDown()}
                    onPointerUp={() => gameControls?.stopMovement()}
                  >
                    ↓
                  </button>
                  <button
                    className={gameCss.controlsArrowKeys}
                    onPointerDown={() => gameControls?.moveRight()}
                    onPointerUp={() => gameControls?.stopMovement()}
                  >
                    →
                  </button>
                </Flex>
                {blockProps.gameType === GameType.CLEANUP_CREW &&
                  gameControls?.resetLevel && (
                    <Flex justify="center" pt="xs">
                      <button
                        className={gameCss.controlsResetButton}
                        onClick={() => gameControls?.resetLevel?.()}
                      >
                        Reset (R)
                      </button>
                    </Flex>
                  )}
              </Flex>
            )}
          </Flex>
        );

      case GameState.Reward:
        return (
          <Flex
            align="center"
            dir="column"
            flex={1}
            gap="lg"
            justify="center"
            p="lg"
          >
            {showCredits ? (
              // Credits screen
              <>
                <Text fontSize="lg" fontWeight="bold" style={headerStyles}>
                  Credits
                </Text>
                <Text fontSize="md" style={headerStyles} textAlign="center">
                  {game.strings.credits &&
                    game.strings.credits.split("\n").map((line, index) => (
                      <span key={index}>
                        {line}
                        {index <
                          game.strings.credits!.split("\n").length - 1 && (
                          <br />
                        )}
                      </span>
                    ))}
                </Text>
                <Flex gap="md">
                  <button
                    className={gameCss.lightGameButton}
                    onClick={() => setShowCredits(false)}
                  >
                    Back
                  </button>
                  <button
                    className={gameCss.playAgainButton}
                    onClick={handlePlayAgain}
                  >
                    Play Again
                  </button>
                </Flex>
              </>
            ) : (
              // Game over or victory screen
              <>
                <Text fontSize="xl" fontWeight="bold" style={headerStyles}>
                  {victoryState === VictoryState.VICTORY
                    ? (game.strings.victory ?? "🎉 Congratulations! 🎉")
                    : (game.strings.failure ?? "💀 Game Over! 💀")}
                </Text>

                {blockProps.rewardSettings?.discountId &&
                  blockProps.rewardSettings?.showRewardOnVictory &&
                  victoryState === VictoryState.VICTORY && (
                    <Flex align="center" dir="column" gap="sm">
                      <Text
                        fontSize="md"
                        style={headerStyles}
                        textAlign="center"
                      >
                        {blockProps.rewardSettings.rewardMessage ??
                          "You've earned a discount!"}
                      </Text>
                      <DiscountCodeDisplay
                        discountId={blockProps.rewardSettings.discountId}
                      />
                    </Flex>
                  )}

                {score !== undefined && (
                  <Text
                    fontSize="lg"
                    fontWeight="semibold"
                    style={headerStyles}
                  >
                    Final Score: {score}
                  </Text>
                )}

                <Flex gap="md">
                  {game.strings.credits && (
                    <button
                      className={gameCss.lightGameButton}
                      onClick={() => setShowCredits(true)}
                    >
                      Credits
                    </button>
                  )}
                  <button
                    className={gameCss.playAgainButton}
                    onClick={handlePlayAgain}
                  >
                    Play Again
                  </button>
                </Flex>
              </>
            )}
          </Flex>
        );

      default:
        return null;
    }
  };

  return (
    <BlockWrapper
      environment={environment}
      inert={false}
      pageSettings={pageSettings}
      withBorder
    >
      {renderContent()}
    </BlockWrapper>
  );
});
